/**
 * QrSettleSheet — iter119.
 *
 * Receiver-side QR generator. Shown when the CREDITOR (the one owed money)
 * taps "Show QR" on a debt card. Encodes a JSON payload the debtor's scanner
 * decodes to instantly create + auto-approve an ₹ transfer.
 *
 * Payload shape (JSON, base64-url-safe optional, fits comfortably in a QR):
 *   {
 *     "v": 1,                  // schema version
 *     "kind": "spliteasy_settle",
 *     "group_id": "...",       // which group's ledger to touch
 *     "to_user_id": "...",     // RECEIVER (the QR owner)
 *     "to_name": "Hitesh",     // for the payer's confirmation screen
 *     "amount": 1500,          // optional — omit for "open" QR
 *     "note": "Court fees",    // optional
 *     "ts": 1740000000         // issued-at (epoch s) for stale-QR protection
 *   }
 *
 * No PII beyond name + user-id; transferable safely.
 */
import { useMemo } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { CheckCircle2, ScanLine } from 'lucide-react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from './ui/sheet';
import { Card } from './ui/card';
import { formatNXC } from '../lib/formatNXC';
import { buildQrUrl } from '../lib/qrPayload';

export default function QrSettleSheet({
  open,
  onOpenChange,
  groupId,
  groupName,
  toUserId,
  toName,
  amount,
  note,
}) {
  const payload = useMemo(() => {
    if (!toUserId || !groupId) return null;
    return buildQrUrl({
      v: 1,
      kind: 'spliteasy_settle',
      group_id: groupId,
      to_user_id: toUserId,
      to_name: toName || 'Member',
      amount: amount && amount > 0 ? Number(amount) : undefined,
      note: note || undefined,
      ts: Math.floor(Date.now() / 1000),
    });
  }, [groupId, toUserId, toName, amount, note]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-3xl" data-testid="qr-settle-sheet">
        <SheetHeader>
          <SheetTitle className="font-display text-xl inline-flex items-center gap-2">
            <ScanLine className="w-5 h-5" />
            {amount > 0 ? `Receive ${formatNXC(amount)}` : 'Receive ₹'}
          </SheetTitle>
          <SheetDescription>
            Ask {toName ? toName : 'the payer'} to scan this QR in their SplitEasy app.
            Their ledger updates instantly — no real money moves.
          </SheetDescription>
        </SheetHeader>

        <div className="py-6 flex flex-col items-center gap-4">
          {payload && (
            <Card className="p-4 bg-white border-2 border-border rounded-3xl" data-testid="qr-settle-code">
              <QRCodeSVG
                value={payload}
                size={240}
                level="M"
                includeMargin={false}
                bgColor="#ffffff"
                fgColor="#0a0a0a"
              />
            </Card>
          )}
          <div className="text-center space-y-1">
            <p className="font-display text-lg font-bold">{toName}</p>
            <p className="text-xs text-muted-foreground">{groupName}</p>
            {amount > 0 && (
              <p className="text-sm font-semibold text-primary tabular-nums" data-testid="qr-settle-amount">
                {formatNXC(amount)}
              </p>
            )}
            {note && (
              <p className="text-xs text-muted-foreground italic">"{note}"</p>
            )}
          </div>
          <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3 text-emerald-500" />
            Auto-settles on scan. Reversible from the ledger if needed.
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
